/**
 * Supplier Backorder Pipeline - Demand-Response View
 * Shows backorder requests from supplier's perspective with columns:
 * - Requests from Hospitals (new opportunities)
 * - Pending/Open (supplier engaged but not fulfilled)
 * - Fulfilled by You (orders you've completed)
 * - Fulfilled by Others (another supplier won)
 * - Closed (cancelled/expired)
 */

import { useState, useEffect, useCallback } from "react";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragOverlay,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "sonner";
import {
  Package,
  Truck,
  CheckCircle,
  Clock,
  AlertTriangle,
  FileText,
  DollarSign,
  Send,
  X,
  GripVertical,
  Building2,
  User,
  Calendar,
  Eye,
  Loader2,
  TrendingUp,
  Users,
  Award
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Supplier-specific pipeline stages
const SUPPLIER_STAGES = [
  { 
    key: "new_requests", 
    label: "Requests from Hospitals", 
    icon: Building2, 
    color: "bg-blue-500",
    description: "New opportunities to quote"
  },
  { 
    key: "pending", 
    label: "Pending / Open", 
    icon: Clock, 
    color: "bg-amber-500",
    description: "You've engaged, awaiting decision"
  },
  { 
    key: "fulfilled_by_you", 
    label: "Fulfilled by You", 
    icon: Award, 
    color: "bg-emerald-500",
    description: "Orders you've won and completed"
  },
  { 
    key: "fulfilled_by_others", 
    label: "Fulfilled by Others", 
    icon: Users, 
    color: "bg-slate-400",
    description: "Another supplier was selected"
  },
  { 
    key: "closed", 
    label: "Closed", 
    icon: X, 
    color: "bg-red-400",
    description: "Cancelled or expired requests"
  }
];

// Draggable Card Component
function DraggableCard({ item, children, canDrag = false }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ 
    id: item.id,
    disabled: !canDrag
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md transition-shadow ${
        isDragging ? "ring-2 ring-blue-500" : ""
      }`}
    >
      <div className="flex items-start gap-2 p-3">
        {canDrag && (
          <div 
            {...attributes} 
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-slate-100 rounded"
          >
            <GripVertical className="w-4 h-4 text-slate-400" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          {children}
        </div>
      </div>
    </div>
  );
}

// Backorder Item Card for Supplier View
function SupplierBackorderCard({ item, onViewDetails, onSubmitQuote, myQuotes = [] }) {
  const hasMyQuote = myQuotes.some(q => q.backorder_id === item.id);
  const myQuote = myQuotes.find(q => q.backorder_id === item.id);
  const isTrending = item.demand_count && item.demand_count >= 3;
  
  return (
    <div className="space-y-2">
      {/* Trending Badge */}
      {isTrending && (
        <div className="flex items-center gap-1 text-xs font-medium text-orange-600 bg-orange-50 px-2 py-1 rounded">
          <TrendingUp className="w-3 h-3" />
          {item.demand_count} Hospitals Searching
        </div>
      )}
      
      {/* Item Info */}
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          <p className="font-medium text-slate-900 truncate">
            {item.items?.[0]?.name || item.item_name || "Backorder Request"}
          </p>
          <p className="text-xs text-slate-500">
            {item.items?.[0]?.part_number || item.part_number || "No part #"}
          </p>
        </div>
        {item.priority === "urgent" || item.priority === "critical" ? (
          <span className="px-2 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
            {item.priority.toUpperCase()}
          </span>
        ) : null}
      </div>
      
      {/* Hospital Info */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Building2 className="w-3 h-3" />
        <span className="truncate">{item.hospital_name || "Hospital"}</span>
      </div>
      
      {/* Quantity */}
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Package className="w-3 h-3" />
        <span>Qty: {item.items?.[0]?.quantity || item.quantity || 1}</span>
      </div>
      
      {/* Date */}
      <div className="flex items-center gap-2 text-xs text-slate-400">
        <Calendar className="w-3 h-3" />
        <span>{new Date(item.created_at).toLocaleDateString()}</span>
      </div>
      
      {/* My Quote Status (if submitted) */}
      {hasMyQuote && myQuote && (
        <div className={`text-xs px-2 py-1 rounded ${
          myQuote.status === 'accepted' || myQuote.status === 'won' ? 'bg-emerald-50 text-emerald-700' :
          myQuote.status === 'rejected' ? 'bg-red-50 text-red-600' :
          'bg-blue-50 text-blue-600'
        }`}>
          <DollarSign className="w-3 h-3 inline mr-1" />
          Your Quote: ${myQuote.customer_price?.toFixed(2) || myQuote.unit_price?.toFixed(2)} - {myQuote.status}
        </div>
      )}
      
      {/* Action Buttons */}
      <div className="flex gap-2 pt-2 border-t border-slate-100">
        <Button 
          variant="ghost" 
          size="sm" 
          className="flex-1 text-xs"
          onClick={() => onViewDetails?.(item)}
        >
          <Eye className="w-3 h-3 mr-1" />
          View
        </Button>
        {!hasMyQuote && (
          <Button 
            size="sm" 
            className="flex-1 text-xs bg-emerald-600 hover:bg-emerald-700"
            onClick={() => onSubmitQuote?.(item)}
          >
            <DollarSign className="w-3 h-3 mr-1" />
            Quote
          </Button>
        )}
      </div>
    </div>
  );
}

// Droppable Column Component
function DroppableColumn({ stage, items, myQuotes, onViewDetails, onSubmitQuote }) {
  const StageIcon = stage.icon;
  
  return (
    <div className="flex-1 min-w-[280px] max-w-[320px] flex flex-col">
      {/* Column Header */}
      <div className={`${stage.color} text-white px-4 py-3 rounded-t-lg`}>
        <div className="flex items-center gap-2">
          <StageIcon className="w-4 h-4" />
          <span className="font-medium">{stage.label}</span>
          <span className="ml-auto bg-white/20 px-2 py-0.5 rounded-full text-xs">
            {items.length}
          </span>
        </div>
        <p className="text-xs text-white/80 mt-1">{stage.description}</p>
      </div>
      
      {/* Column Content */}
      <div className="flex-1 bg-slate-50 rounded-b-lg p-2 min-h-[400px] max-h-[600px] overflow-y-auto">
        <SortableContext items={items.map(i => i.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-2">
            {items.map((item) => (
              <DraggableCard key={item.id} item={item} canDrag={false}>
                <SupplierBackorderCard 
                  item={item} 
                  myQuotes={myQuotes}
                  onViewDetails={onViewDetails}
                  onSubmitQuote={onSubmitQuote}
                />
              </DraggableCard>
            ))}
            {items.length === 0 && (
              <div className="text-center py-8 text-slate-400 text-sm">
                No items in this stage
              </div>
            )}
          </div>
        </SortableContext>
      </div>
    </div>
  );
}

// Main Supplier Pipeline Component
export default function SupplierBackorderPipeline({ 
  supplierId,
  authToken,
  onRefresh = null,
  onSubmitQuote = null
}) {
  const [items, setItems] = useState([]);
  const [myQuotes, setMyQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailItem, setDetailItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  // DnD sensors (read-only for now, but structure in place)
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor)
  );

  // Fetch backorder requests and supplier's quotes
  const fetchData = useCallback(async () => {
    if (!authToken) return;
    
    setLoading(true);
    try {
      // Fetch available requests
      const requestsRes = await fetch(`${API_URL}/api/suppliers/quote-requests`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      // Fetch my quotes
      const quotesRes = await fetch(`${API_URL}/api/suppliers/quotes`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (requestsRes.ok) {
        const requestsData = await requestsRes.json();
        setItems(requestsData.requests || []);
      }
      
      if (quotesRes.ok) {
        const quotesData = await quotesRes.json();
        setMyQuotes(quotesData.quotes || []);
      }
    } catch (error) {
      console.error("Failed to fetch pipeline data:", error);
      toast.error("Failed to load pipeline data");
    } finally {
      setLoading(false);
    }
  }, [authToken]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Categorize items into pipeline stages
  const categorizeItems = () => {
    const quotedRequestIds = new Set(myQuotes.map(q => q.backorder_id));
    const wonQuoteRequestIds = new Set(
      myQuotes.filter(q => q.status === 'accepted' || q.status === 'won').map(q => q.backorder_id)
    );
    
    const stages = {
      new_requests: [],
      pending: [],
      fulfilled_by_you: [],
      fulfilled_by_others: [],
      closed: []
    };
    
    items.forEach(item => {
      const status = item.status?.toLowerCase() || '';
      const hasQuoted = quotedRequestIds.has(item.id);
      const wonThisOne = wonQuoteRequestIds.has(item.id);
      
      // Closed/Cancelled
      if (status === 'cancelled' || status === 'closed' || status === 'expired') {
        stages.closed.push(item);
      }
      // Fulfilled by me (I won this order)
      else if (wonThisOne || (hasQuoted && (status === 'completed' || status === 'fulfilled' || status === 'delivered'))) {
        stages.fulfilled_by_you.push(item);
      }
      // Fulfilled by others (completed but I didn't win)
      else if (!wonThisOne && (status === 'completed' || status === 'fulfilled' || status === 'delivered')) {
        stages.fulfilled_by_others.push(item);
      }
      // Pending (I've quoted but waiting for decision)
      else if (hasQuoted) {
        stages.pending.push(item);
      }
      // New requests (haven't quoted yet)
      else {
        stages.new_requests.push(item);
      }
    });
    
    return stages;
  };

  const stageItems = categorizeItems();

  // Handle view details
  const handleViewDetails = (item) => {
    setDetailItem(item);
    setShowDetailModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-slate-600">Loading pipeline...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Pipeline Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">Backorder Pipeline</h2>
          <p className="text-sm text-slate-500">
            {items.length} total requests • {myQuotes.length} quotes submitted
          </p>
        </div>
        <Button variant="outline" onClick={fetchData}>
          <Package className="w-4 h-4 mr-2" />
          Refresh
        </Button>
      </div>

      {/* Pipeline Columns */}
      <DndContext sensors={sensors} collisionDetection={closestCenter}>
        <div className="flex gap-4 overflow-x-auto pb-4">
          {SUPPLIER_STAGES.map((stage) => (
            <DroppableColumn
              key={stage.key}
              stage={stage}
              items={stageItems[stage.key] || []}
              myQuotes={myQuotes}
              onViewDetails={handleViewDetails}
              onSubmitQuote={onSubmitQuote}
            />
          ))}
        </div>
      </DndContext>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Request Details</DialogTitle>
            <DialogDescription>
              View full details for this backorder request
            </DialogDescription>
          </DialogHeader>
          
          {detailItem && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-slate-500">Hospital</p>
                  <p className="font-medium">{detailItem.hospital_name || "N/A"}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Request ID</p>
                  <p className="font-mono text-sm">{detailItem.id?.substring(0, 12)}...</p>
                </div>
              </div>
              
              <div>
                <p className="text-xs text-slate-500 mb-2">Items Requested</p>
                {detailItem.items?.map((item, idx) => (
                  <div key={idx} className="bg-slate-50 p-3 rounded-lg mb-2">
                    <p className="font-medium">{item.name || item.item_name}</p>
                    <p className="text-sm text-slate-600">Part #: {item.part_number || "N/A"}</p>
                    <p className="text-sm text-slate-600">Qty: {item.quantity}</p>
                  </div>
                ))}
              </div>
              
              {detailItem.notes && (
                <div>
                  <p className="text-xs text-slate-500">Notes</p>
                  <p className="text-sm text-slate-600">{detailItem.notes}</p>
                </div>
              )}
              
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowDetailModal(false)}>
                  Close
                </Button>
                {onSubmitQuote && !myQuotes.some(q => q.backorder_id === detailItem.id) && (
                  <Button 
                    className="bg-emerald-600 hover:bg-emerald-700"
                    onClick={() => {
                      setShowDetailModal(false);
                      onSubmitQuote(detailItem);
                    }}
                  >
                    <DollarSign className="w-4 h-4 mr-2" />
                    Submit Quote
                  </Button>
                )}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
